import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn } from '../../lib/utils';
import { Coins, TrendingDown, TrendingUp, Mic, ScanLine, MessageSquare, Calendar, ArrowRight, DollarSign, CheckCircle2, Sparkles, Zap, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useAppMode } from '../../contexts/AppModeContext';

const USAGE_HISTORY = [
  { id: 1, date: '2026-02-03', action: 'Ambient Listening \u2014 John Smith', type: 'ambient', duration: '18 min', credits: 18 },
  { id: 2, date: '2026-02-03', action: 'Ambient Listening \u2014 Maria Garcia', type: 'ambient', duration: '12 min', credits: 12 },
  { id: 3, date: '2026-02-03', action: 'Dictation \u2014 Robert Johnson', type: 'dictation', duration: '8 min', credits: 8 },
  { id: 4, date: '2026-02-03', action: 'Lumina Lens \u2014 Lab Report (3 pages)', type: 'lens', duration: '3 pages', credits: 3 },
  { id: 5, date: '2026-02-03', action: 'Dictation \u2014 Emily Davis', type: 'dictation', duration: '15 min', credits: 15 },
  { id: 6, date: '2026-02-02', action: 'Ambient Listening \u2014 Sarah Williams', type: 'ambient', duration: '22 min', credits: 22 },
  { id: 7, date: '2026-02-02', action: 'Lumina Lens \u2014 Referral Notes (2 pages)', type: 'lens', duration: '2 pages', credits: 2 },
  { id: 8, date: '2026-02-02', action: 'Ambient Listening \u2014 Michael Brown', type: 'ambient', duration: '14 min', credits: 14 },
  { id: 9, date: '2026-02-01', action: 'Dictation \u2014 Lisa Anderson', type: 'dictation', duration: '10 min', credits: 10 },
  { id: 10, date: '2026-02-01', action: 'Lumina Lens \u2014 CCDA File (8 pages)', type: 'lens', duration: '8 pages', credits: 8 },
];

const DAILY_CHART_DATA = [
  { date: 'Mon', ambient: 28, dictation: 8, lens: 6 },
  { date: 'Tue', ambient: 35, dictation: 12, lens: 9 },
  { date: 'Wed', ambient: 22, dictation: 10, lens: 6 },
  { date: 'Thu', ambient: 40, dictation: 15, lens: 6 },
  { date: 'Fri', ambient: 30, dictation: 15, lens: 8 },
];

const LuminaCredits = () => {
  const { isDemo } = useAppMode();
  const [balance] = useState(247);
  const [customAmount, setCustomAmount] = useState('');
  const [showDollar, setShowDollar] = useState(false);
  const usedToday = 56;
  const usedThisWeek = 250;
  const avgDaily = 50;
  const subscriptionThreshold = 500;
  const currentMonthUsage = 430;

  const getTypeIcon = (type) => {
    const map = { ambient: Mic, dictation: MessageSquare, lens: ScanLine };
    const Icon = map[type] || Mic;
    return <Icon className="h-4 w-4 text-slate-500" />;
  };

  const getTypeColor = (type) => {
    const map = { ambient: 'bg-violet-50 text-violet-700', dictation: 'bg-blue-50 text-blue-700', lens: 'bg-amber-50 text-amber-700' };
    return map[type] || map.ambient;
  };

  const formatVal = (v) => showDollar ? `$${v.toFixed(2)}` : v;

  return (
    <div className="space-y-6" data-testid="credits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Credits & Usage</h1>
          <p className="text-slate-500 mt-1"><span className="font-semibold text-slate-700">1 Credit = $1.00</span> &middot; Monitor your usage and manage your balance</p>
        </div>
        <Button className="bg-black hover:bg-gray-800 gap-2" onClick={() => toast.success('Credits added')} data-testid="add-credits-btn">
          <Coins className="h-4 w-4" /> Add Credits
        </Button>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-2 border-amber-200 bg-amber-50/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><Coins className="h-5 w-5 text-amber-500" /><span className="text-xs font-medium text-amber-700">Balance</span></div>
            <p className="text-3xl font-bold text-slate-900" data-testid="credit-balance">{balance}</p>
            <p className="text-xs text-muted-foreground mt-1">Credits (${balance.toFixed(2)})</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><TrendingDown className="h-5 w-5 text-red-500" /><span className="text-xs font-medium text-slate-500">Today</span></div>
            <p className="text-3xl font-bold text-slate-900">{usedToday}</p>
            <p className="text-xs text-muted-foreground mt-1">Credits (${usedToday.toFixed(2)}) used today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><Calendar className="h-5 w-5 text-blue-500" /><span className="text-xs font-medium text-slate-500">This Week</span></div>
            <p className="text-3xl font-bold text-slate-900">{usedThisWeek}</p>
            <p className="text-xs text-muted-foreground mt-1">Credits (${usedThisWeek.toFixed(2)}) this week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><TrendingUp className="h-5 w-5 text-violet-500" /><span className="text-xs font-medium text-slate-500">Daily Avg</span></div>
            <p className="text-3xl font-bold text-slate-900">{avgDaily}</p>
            <p className="text-xs text-muted-foreground mt-1">~{Math.floor(balance / avgDaily)} days remaining</p>
          </CardContent>
        </Card>
      </div>

      {/* Custom Credit Purchase */}
      <Card data-testid="custom-purchase-card">
        <CardHeader><CardTitle className="text-base font-semibold">Add Credits (Pay-as-you-go)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* Quick-add preset buttons */}
          <div className="grid grid-cols-4 gap-2">
            {[50, 100, 200, 500].map(amt => (
              <button key={amt} onClick={() => { toast.success(`$${amt} added (${amt} credits)`); }}
                className="p-3 rounded-xl border-2 border-slate-200 hover:border-slate-400 text-center transition-all"
                data-testid={`quick-add-${amt}`}>
                <p className="text-lg font-bold text-slate-900">{amt}</p>
                <p className="text-[10px] text-muted-foreground">${amt}</p>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[11px] text-slate-400 font-medium">Or enter a custom amount</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Custom input */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600">Enter a custom amount</label>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400">$</span>
              <Input type="number" placeholder="0.00" className="pl-7" value={customAmount} onChange={e => setCustomAmount(e.target.value)} data-testid="custom-amount-input" />
            </div>
            {customAmount && Number(customAmount) > 0 && (
              <p className="text-sm text-slate-600" data-testid="credit-estimate">
                = <span className="font-semibold">{Math.round(Number(customAmount))} credits</span> &middot; covers approx. <span className="font-semibold">{Math.round(Number(customAmount) / avgDaily)} days</span> at your current usage rate
              </p>
            )}
            <Button className="bg-black hover:bg-gray-800 gap-2" disabled={!customAmount || Number(customAmount) <= 0}
              onClick={() => { if (customAmount) { toast.success(`$${customAmount} added (${Math.round(Number(customAmount))} credits)`); setCustomAmount(''); }}}
              data-testid="purchase-credits-btn">
              <Coins className="h-4 w-4" /> Add {customAmount && Number(customAmount) > 0 ? `${Math.round(Number(customAmount))} credits for $${Number(customAmount).toFixed(2)}` : 'Credits'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Pricing Card */}
      <div className="relative rounded-xl p-5" style={{ background: '#fafafa', border: '0.5px solid #e8e8e8' }} data-testid="subscription-pricing-card">
        {/* Lock / Check icon */}
        <div className="absolute top-4 right-4">
          {currentMonthUsage >= subscriptionThreshold
            ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            : <Lock className="h-5 w-5 text-slate-300" />
          }
        </div>

        <h3 className="text-[15px] font-medium text-slate-900">Subscription pricing</h3>
        <p className="text-xs text-slate-500 mt-1">Unlock when your usage reaches 500 credits/month</p>
        <p className="text-[13px] text-slate-600 mt-3 leading-relaxed max-w-xl">
          High-usage practices can switch to a flat monthly subscription and save up to 20% vs. pay-as-you-go. Subscription pricing activates automatically on your next billing cycle once you hit the usage threshold — no action needed.
        </p>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs text-slate-500">Your usage this month: <span className="font-semibold text-slate-700">{currentMonthUsage} / 500 credits</span></p>
          </div>
          <div className="h-1 rounded-full overflow-hidden" style={{ background: '#eeeeee' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min((currentMonthUsage / subscriptionThreshold) * 100, 100)}%`, background: '#5040c8' }} />
          </div>
        </div>

        {/* CTA */}
        <div className="mt-4">
          {currentMonthUsage >= subscriptionThreshold ? (
            <Button className="bg-black hover:bg-gray-800 text-white gap-2"
              onClick={() => toast.success('Subscription activated! You save 20%.')}
              data-testid="activate-subscription-btn">
              Activate subscription — save 20%
            </Button>
          ) : (
            <Button className="bg-slate-200 text-slate-400 cursor-not-allowed gap-2" disabled data-testid="subscription-locked-btn">
              <Lock className="h-3.5 w-3.5" /> Unlocks at 500 cr/month
            </Button>
          )}
          <p className="text-[11px] text-slate-400 mt-2">
            Subscription billing begins on your next monthly cycle. You can revert to pay-as-you-go at any time.
          </p>
        </div>
      </div>

      {/* Pricing Reference */}
      <Card>
        <CardHeader><CardTitle className="text-base font-semibold">Credit Pricing</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-4 p-4 rounded-xl border">
              <div className="h-10 w-10 rounded-lg bg-violet-50 flex items-center justify-center"><Mic className="h-5 w-5 text-violet-600" /></div>
              <div><p className="text-sm font-semibold">Ambient Listening</p><p className="text-xs text-muted-foreground">1 credit ($1.00) per minute</p></div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-xl border">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><MessageSquare className="h-5 w-5 text-blue-600" /></div>
              <div><p className="text-sm font-semibold">Dictation</p><p className="text-xs text-muted-foreground">1 credit ($1.00) per minute</p></div>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-xl border">
              <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center"><ScanLine className="h-5 w-5 text-amber-600" /></div>
              <div><p className="text-sm font-semibold">Lumina Lens</p><p className="text-xs text-muted-foreground">1 credit ($1.00) per page</p></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Chart (Fixed) */}
      <Card data-testid="weekly-chart">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">This Week</CardTitle>
            <div className="flex gap-1">
              <Button size="sm" variant={!showDollar ? 'default' : 'outline'} className={cn("text-[10px] h-6 px-2", !showDollar && "bg-slate-900")} onClick={() => setShowDollar(false)}>Credits</Button>
              <Button size="sm" variant={showDollar ? 'default' : 'outline'} className={cn("text-[10px] h-6 px-2", showDollar && "bg-slate-900")} onClick={() => setShowDollar(true)}>Dollars</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={DAILY_CHART_DATA} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => showDollar ? `$${v}` : v} />
              <Tooltip formatter={(v) => showDollar ? `$${v.toFixed(2)}` : `${v} credits`} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="ambient" name="Ambient" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="dictation" name="Dictation" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="lens" name="Lens" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Usage History */}
      <Card>
        <CardHeader><CardTitle className="text-base font-semibold">Usage History</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Activity</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="text-right">Credits</TableHead>
                <TableHead className="text-right">Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {USAGE_HISTORY.map((item) => (
                <TableRow key={item.id} data-testid={`usage-row-${item.id}`}>
                  <TableCell className="text-sm text-muted-foreground">{item.date}</TableCell>
                  <TableCell className="text-sm font-medium">{item.action}</TableCell>
                  <TableCell><Badge className={cn("text-xs capitalize", getTypeColor(item.type))}>{item.type}</Badge></TableCell>
                  <TableCell className="text-sm">{item.duration}</TableCell>
                  <TableCell className="text-right font-medium">{item.credits}</TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">${item.credits.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default LuminaCredits;
